import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Separator, Text, useSlotRecipe} from '@chakra-ui/react3'
import ModuleContainer from '../../components/module-container'
import Heading from '../../components/cms/heading'
import Image from '../../components/cms/image'
import productColorsSlotRecipe from './index.recipe'
import useSharedLibraryAssetUrl from '../../../hooks/useSharedLibraryAssetUrl'
import {hasValidImageSrc} from '../../utils/utils'
import {FormattedMessage, useIntl} from 'react-intl'
import Fade from '../../components/cms/fade'
import {useReducedMotion} from 'framer-motion'
import {MODULE_TYPE_LABELS, MODULE_TYPES} from '../constants'

/**
 * Product Colors module displays a color palette exploration interface with interactive color swatches
 * and corresponding product images. Users can select different colors to view how the product looks
 * in each available color variant.
 *
 * @component
 * @param {string} [anchor] - Optional anchor ID for deep linking to this module
 * @param {'default'|'subtle'|'strong'|'inverse'} [background='default'] - Background style for the module
 * @param {object} [heading] - Optional heading configuration object with subline, headline1, headline2, etc.
 * @param {number} [headingLevel=2] - HTML heading level (1-6) for semantic structure
 * @param {object} colorProduct - Color product configuration object containing:
 *   @param {string} colorProduct.productSetName - Product set name used to construct image paths (e.g., "YOYO_FRAME")
 *   @param {Array<object>} colorProduct.colors - Array of color objects, each containing:
 *     @param {string} colorProduct.colors[].name - Display name of the color (e.g., "Peppermint")
 *     @param {string} colorProduct.colors[].colorCode - Color code used in image filename (e.g., "C1E4D4")
 *     @param {string} colorProduct.colors[].swatch - Swatch image URL
 *     @param {string} colorProduct.colors[].alt - Title/alt text for the swatch
 * @param {object} [multiColorImage] - Optional multi-color image configuration displayed as first swatch
 *   @param {string} [multiColorImage.src] - Multi-color product image URL
 *   @param {string} [multiColorImage.disBaseLink] - DIS base link for image
 *   @param {string} [multiColorImage.title] - Image title
 *   @param {string} [multiColorImage.alt] - Image alt text
 * @param {'20'|'30'|'40'|'50'} [surfaceHeight='20'] - Height percentage of the desktop background surface gradient (20%, 30%, 40%, or 50%)
 * @param {'10'|'20'|'30'|'40'} [surfaceHeightMobile='20'] - Height percentage of the mobile background surface gradient (10%, 20%, 30% or 40%)
 * @returns {JSX.Element} Rendered ProductColors module with interactive color selection
 */
const ProductColors = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            anchor,
            background,
            heading,
            headingLevel = 2,
            colorProduct: {colors, productSetName, productDisplayName},
            multiColorImage,
            surfaceHeight = '50'
        },
        ref
    ) => {
        const hasMultiColorImage = hasValidImageSrc(multiColorImage)
        const reducedMotionEnabled = useReducedMotion()
        const recipe = useSlotRecipe({recipe: productColorsSlotRecipe})
        const styles = useMemo(
            () => recipe({hasMultiColorImage, surfaceHeight}),
            [recipe, hasMultiColorImage, surfaceHeight]
        )

        const imagesPrefix = useSharedLibraryAssetUrl(
            useMemo(
                () =>
                    `cms-modules/product-colors/${productSetName.toLowerCase().replace('_', '-')}`,
                [productSetName]
            )
        )

        // -1 means the multi color image, otherwise the index from colorProduct.colors array
        const [selectedVariant, setSelectedVariant] = useState(hasMultiColorImage ? -1 : 0)
        const [hideAll, setHideAll] = useState(false)
        const [nextVariant, setNextVariant] = useState(null)
        const isSwitching = nextVariant !== null

        const changeVariant = (next) => {
            if (next === selectedVariant) return

            if (reducedMotionEnabled) {
                setNextVariant(null)
                setHideAll(false)
                setSelectedVariant(next)
                return
            }

            const isSingleToSingle = selectedVariant !== -1 && next !== -1
            if (isSingleToSingle) {
                setSelectedVariant(next)
                return
            }

            // multi <-> single: no overlap
            setNextVariant(next)
            setHideAll(true)
        }

        const {formatMessage} = useIntl()

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                css={styles.root}
                ref={ref}
                spacing="none"
                modulePadding="productColors"
            >
                <Text className="sr-only">
                    <FormattedMessage
                        defaultMessage="This is a preview of product color options. Use the different buttons to get a preview of colors available for {productDisplayName}."
                        id="leap.product-colors.assistive_msg"
                        values={{productDisplayName}}
                    />
                </Text>
                <Box px={'spacing-24'}>
                    <Fade>
                        <Heading
                            {...heading}
                            headingLevel={headingLevel}
                            alignment="center"
                            type={formatMessage(MODULE_TYPE_LABELS[MODULE_TYPES.PRODUCT])}
                        />
                    </Fade>
                </Box>
                <Box css={styles.bgSurface} />
                <Box css={styles.spotlightLight}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="848"
                        height="991"
                        viewBox="0 0 848 991"
                        fill="none"
                    >
                        <g opacity="0.04" filter="url(#filter0_f_9520_813)">
                            <path d="M120 871L417.545 120H433.918L728 871H120Z" fill="white" />
                        </g>
                        <defs>
                            <filter
                                id="filter0_f_9520_813"
                                x="0"
                                y="0"
                                width="848"
                                height="991"
                                filterUnits="userSpaceOnUse"
                                colorInterpolationFilters="sRGB"
                            >
                                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                <feBlend
                                    mode="normal"
                                    in="SourceGraphic"
                                    in2="BackgroundImageFix"
                                    result="shape"
                                />
                                <feGaussianBlur
                                    stdDeviation="60"
                                    result="effect1_foregroundBlur_9520_813"
                                />
                            </filter>
                        </defs>
                    </svg>
                </Box>
                <Box css={styles.spotlightDark}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="666"
                        height="911"
                        viewBox="0 0 666 911"
                        fill="none"
                    >
                        <g opacity="0.04" filter="url(#filter0_f_6013_16672)">
                            <path d="M-62 791L235.545 40H251.918L546 791H-62Z" fill="white" />
                        </g>
                        <defs>
                            <filter
                                id="filter0_f_6013_16672"
                                x="-182"
                                y="-80"
                                width="848"
                                height="991"
                                filterUnits="userSpaceOnUse"
                                colorInterpolationFilters="sRGB"
                            >
                                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                <feBlend
                                    mode="normal"
                                    in="SourceGraphic"
                                    in2="BackgroundImageFix"
                                    result="shape"
                                />
                                <feGaussianBlur
                                    stdDeviation="60"
                                    result="effect1_foregroundBlur_6013_16672"
                                />
                            </filter>
                        </defs>
                    </svg>
                </Box>
                <Box
                    css={[styles.imagesContainer, hideAll && styles.imagesContainerHidden]}
                    onTransitionEnd={(e) => {
                        if (e.target !== e.currentTarget) return
                        if (e.propertyName !== 'opacity') return
                        if (!isSwitching) return

                        if (hideAll) {
                            setSelectedVariant(nextVariant)
                            requestAnimationFrame(() => setHideAll(false))
                        } else {
                            setNextVariant(null)
                        }
                    }}
                >
                    {hasMultiColorImage && (
                        <Box
                            hidden={selectedVariant !== -1}
                            css={[
                                styles.multiImageContainer,
                                selectedVariant === -1
                                    ? styles.multiColorImageActive
                                    : styles.multiColorImageInactive,
                                isSwitching && styles.imageContainerNoTransition
                            ]}
                        >
                            <Box css={styles.multiColorImageFrame}>
                                <Image
                                    {...multiColorImage}
                                    alt={multiColorImage.isDecorative ? '' : multiColorImage.alt}
                                    objectFit="cover"
                                    widths={{base: '364px', xs: '890px', s: '1200px'}}
                                    loading={selectedVariant === -1 ? 'eager' : 'lazy'}
                                    fetchpriority={selectedVariant === -1 ? 'high' : 'auto'}
                                />
                            </Box>
                        </Box>
                    )}
                    {colors.map((color, index) => {
                        const imageAlt = color.alt
                            ? color.alt.replace(/,?\s*swatch\s*$/i, '').trim()
                            : ''
                        const isActive = selectedVariant === index

                        return (
                            <Box
                                key={index}
                                hidden={!isActive}
                                css={[
                                    styles.imageContainer,
                                    isActive
                                        ? styles.imageContainerActive
                                        : styles.imageContainerInactive,
                                    isSwitching && styles.imageContainerNoTransition
                                ]}
                            >
                                <Box css={styles.singleImageFrame}>
                                    <Image
                                        src={`${imagesPrefix}/${color.colorCode}.png`}
                                        alt={imageAlt}
                                        objectFit="contain"
                                        widths={{base: '327px', s: '445px'}}
                                        loading={selectedVariant === index ? 'eager' : 'lazy'}
                                        fetchpriority={selectedVariant === index ? 'high' : 'auto'}
                                    />
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
                <Box css={styles.swatchesContainer}>
                    <Text css={styles.labelContainer}>
                        {selectedVariant >= 0 ? (
                            <>
                                <Text as="span" css={styles.label}>
                                    <FormattedMessage
                                        defaultMessage="Colour:"
                                        id="leap.product-colors.label.color"
                                    />
                                    &nbsp;
                                </Text>
                                <Text as="span" css={styles.value}>
                                    {colors[selectedVariant].name}
                                </Text>
                            </>
                        ) : (
                            // need to render something to prevent layout shift when multi-color is selected
                            // maybe this will be updated to render a specific label with followup ticket
                            <>&nbsp;</>
                        )}
                    </Text>
                    <Box css={styles.swatches} data-testid="swatches" as="fieldset">
                        <Text
                            as="legend"
                            css={{
                                // need to change the display from table as table expands based on content
                                // and causes horizontal overflow
                                display: 'block',
                                srOnly: true
                            }}
                        >
                            {formatMessage(
                                {
                                    id: 'leap.product-colors.legend.assistive_msg',
                                    defaultMessage: 'Colors available for {product}'
                                },
                                {product: productDisplayName}
                            )}
                        </Text>
                        {/* Multi-color swatch appears first when multiColorImage is provided */}
                        {hasMultiColorImage && (
                            <>
                                <Button
                                    className="leap-swatch group"
                                    unstyled
                                    css={styles.swatchTouchArea}
                                    {...(selectedVariant === -1 && {'data-active': true})}
                                    onClick={() => changeVariant(-1)}
                                    aria-label={formatMessage(
                                        {
                                            id: 'leap.product-colors.swatch.multi-color.assistive_msg',
                                            defaultMessage: 'Show {product} in Multi-color'
                                        },
                                        {product: productDisplayName}
                                    )}
                                >
                                    <Text css={styles.swatchLabel}>
                                        <FormattedMessage
                                            id="leap.product-colors.swatch.multi-color.label"
                                            defaultMessage="Multi-color"
                                        />
                                    </Text>
                                    <Box css={styles.multiColorSwatch} />
                                </Button>
                                {/* Visual separator between multi-color swatch and individual colors */}
                                <Box css={styles.separatorContainer}>
                                    <Separator
                                        variant="solid"
                                        orientation={'vertical'}
                                        size="sm"
                                        css={styles.separator}
                                    />
                                </Box>
                            </>
                        )}
                        {colors.map((color, index) => (
                            // the swatch has very specific implementation to allow for desktop label and extracting reusable component will make it more complicated.
                            <Button
                                key={index}
                                className="leap-swatch group"
                                unstyled
                                css={styles.swatchTouchArea}
                                {...(index === selectedVariant && {'data-active': true})}
                                onClick={() => changeVariant(index)}
                                aria-label={formatMessage(
                                    {
                                        id: 'leap.product-colors.swatch.color.assistive_msg',
                                        defaultMessage: 'Show {product} in {color}'
                                    },
                                    {
                                        product: productDisplayName,
                                        color: color.name || color.alt
                                    }
                                )}
                            >
                                <Text css={styles.swatchLabel}>{color.name || color.alt}</Text>
                                <Box css={styles.swatch}>
                                    <Box
                                        as="img"
                                        alt={color.alt}
                                        src={color.swatch}
                                        cursor="inherit"
                                        objectFit="cover"
                                        width="100%"
                                        height="100%"
                                    />
                                </Box>
                            </Button>
                        ))}
                    </Box>
                </Box>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    ProductColors.displayName = 'ProductColors'
}

ProductColors.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.string,
    colorProduct: PropTypes.shape({
        productSetName: PropTypes.string,
        productDisplayName: PropTypes.string,
        colors: PropTypes.arrayOf(
            PropTypes.shape({
                colorCode: PropTypes.string,
                swatch: PropTypes.string,
                name: PropTypes.string,
                alt: PropTypes.string
            })
        )
    }),
    heading: PropTypes.object,
    headingLevel: PropTypes.number,
    multiColorImage: PropTypes.shape({
        src: PropTypes.string,
        disBaseLink: PropTypes.string,
        title: PropTypes.string,
        alt: PropTypes.string,
        isDecorative: PropTypes.bool
    }),
    surfaceHeight: PropTypes.string,
    surfaceHeightMobile: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string
}

export default ProductColors
